import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const FAVORITES_STORAGE_KEY = "samfora_favorite_foundations";

// Get favorites from localStorage (fallback for non-authenticated users)
function getLocalFavorites(): string[] {
  try {
    const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function setLocalFavorites(favorites: string[]) {
  localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
}

// Separate hook for getting organization ID
function useOrganizationId() {
  const { data: organizationData, isLoading } = useQuery({
    queryKey: ["user-organization-id"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { organizationId: null, isAuthenticated: false };
      
      const { data: profile } = await supabase
        .from("profiles")
        .select("organization_id")
        .eq("id", user.id)
        .single();
      
      return { 
        organizationId: profile?.organization_id ?? null, 
        isAuthenticated: true 
      };
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    organizationId: organizationData?.organizationId ?? null,
    isAuthenticated: organizationData?.isAuthenticated ?? false,
    isLoading,
  };
}

export function useFavorites() {
  const queryClient = useQueryClient();
  const { organizationId, isAuthenticated, isLoading: isOrgLoading } = useOrganizationId();
  const [localFavorites, setLocalFavoritesState] = useState<string[]>(() => getLocalFavorites());
  const step3MarkedRef = useRef(false);

  // Refetch organization ID on auth state change
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      queryClient.invalidateQueries({ queryKey: ["user-organization-id"] });
    });

    return () => subscription.unsubscribe();
  }, [queryClient]);

  // Fetch favorites from database for authenticated users with an organization
  const { data: dbFavorites = [], isLoading: isFavoritesLoading } = useQuery({
    queryKey: ["organization-favorites", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      const { data, error } = await supabase
        .from("organization_favorites")
        .select("org_number")
        .eq("organization_id", organizationId);
      
      if (error) throw error;
      return data.map((f) => f.org_number);
    },
    enabled: !!organizationId,
  });

  // Clear localStorage once DB favorites are successfully loaded
  // This prevents stale localStorage data from being used
  useEffect(() => {
    if (organizationId && !isFavoritesLoading && dbFavorites.length >= 0) {
      // Only clear if we have an org ID and the query has completed
      const localData = localStorage.getItem(FAVORITES_STORAGE_KEY);
      if (localData) {
        localStorage.removeItem(FAVORITES_STORAGE_KEY);
        setLocalFavoritesState([]);
      }
    }
  }, [organizationId, isFavoritesLoading, dbFavorites]);

  // Add favorite mutation
  const addMutation = useMutation({
    mutationFn: async (orgNumber: string) => {
      if (!organizationId) throw new Error("No organization");
      const { error } = await supabase
        .from("organization_favorites")
        .insert({ organization_id: organizationId, org_number: orgNumber });
      if (error) throw error;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["organization-favorites", organizationId] });
      
      // Check if we've hit 5 favorites and mark step 3 complete
      if (!step3MarkedRef.current && organizationId) {
        const { count } = await supabase
          .from("organization_favorites")
          .select("*", { count: "exact", head: true })
          .eq("organization_id", organizationId);
        
        if ((count || 0) >= 5) {
          step3MarkedRef.current = true;
          // Mark step 3 complete via direct update (to avoid circular dependency)
          await supabase
            .from("organization_onboarding")
            .update({
              step_3_completed: true,
              step_3_completed_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            })
            .eq("organization_id", organizationId)
            .eq("step_3_completed", false); // Only update if not already complete
          
          queryClient.invalidateQueries({ queryKey: ["organization-onboarding", organizationId] });
        }
      }
    },
  });

  // Remove favorite mutation
  const removeMutation = useMutation({
    mutationFn: async (orgNumber: string) => {
      if (!organizationId) throw new Error("No organization");
      const { error } = await supabase
        .from("organization_favorites")
        .delete()
        .eq("organization_id", organizationId)
        .eq("org_number", orgNumber);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization-favorites", organizationId] });
    },
  });

  // Calculate loading state - true when org is loading OR when we have an org but favorites haven't loaded yet
  const isLoading = isOrgLoading || (!!organizationId && isFavoritesLoading);

  // Get the current favorites list based on auth state
  // Only use dbFavorites when we have an organizationId AND favorites have loaded
  const favorites = useMemo(() => {
    if (organizationId && !isFavoritesLoading) {
      return dbFavorites;
    }
    // Fall back to localStorage only when not authenticated or org query is still loading
    if (!isAuthenticated && !isOrgLoading) {
      return localFavorites;
    }
    // While loading, return empty array to prevent stale data usage
    return [];
  }, [organizationId, isFavoritesLoading, dbFavorites, isAuthenticated, isOrgLoading, localFavorites]);

  const addFavorite = useCallback((orgNumber: string) => {
    if (organizationId) {
      addMutation.mutate(orgNumber);
    } else {
      setLocalFavoritesState((prev) => {
        if (prev.includes(orgNumber)) return prev;
        const updated = [...prev, orgNumber];
        setLocalFavorites(updated);
        return updated;
      });
    }
  }, [organizationId, addMutation]);

  const removeFavorite = useCallback((orgNumber: string) => {
    if (organizationId) {
      removeMutation.mutate(orgNumber);
    } else {
      setLocalFavoritesState((prev) => {
        const updated = prev.filter((id) => id !== orgNumber);
        setLocalFavorites(updated);
        return updated;
      });
    }
  }, [organizationId, removeMutation]);

  const toggleFavorite = useCallback((orgNumber: string) => {
    if (favorites.includes(orgNumber)) {
      removeFavorite(orgNumber);
    } else {
      addFavorite(orgNumber);
    }
  }, [favorites, addFavorite, removeFavorite]);

  const isFavorite = useCallback(
    (orgNumber: string) => favorites.includes(orgNumber),
    [favorites]
  );

  return {
    favorites,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    hasFavorites: favorites.length > 0,
    isAuthenticated,
    isLoading,
  };
}
